import { addNotification } from "portal/state/notifications";
import { BLUE_LOADING_BUTTON, classes } from "portal/theme/theme";
import { buildPermission } from "portal/utils/auth";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { Field, Form, Formik } from "formik";
import { GlobalHotKeys } from "react-hotkeys";
import { number, object, string } from "yup";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
  UserDisplayRole,
  userDisplayRoleToJSON,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { Select, TextField } from "formik-mui";
import { useAuthorizationRequired } from "./auth/WithAuthorizationRequired";
import { useCanSeeOtherCustomers } from "portal/views/customers/permissions";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { userApi } from "common/state/portalApi/user";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/AddOutlined";
import InviteIcon from "@mui/icons-material/PersonAddOutlined";
import React, { FunctionComponent, useState } from "react";

interface InviteUserProps {
  hotkey?: string;
}
export const InviteUser: FunctionComponent<InviteUserProps> = ({ hotkey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const canReadCustomers = useCanSeeOtherCustomers();

  const canUpdateAllPermissions = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.users_permissions,
      PermissionDomain.all
    )
  );

  const canUpdateCustomerPermissions = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.users_permissions,
      PermissionDomain.customer
    )
  );

  const canUpdatePermissions =
    canUpdateAllPermissions || canUpdateCustomerPermissions;

  const [inviteUser] = useMutationPopups(
    userApi.endpoints.inviteUser.useMutation(),
    {
      success: capitalize(
        t("utils.actions.invitedLong", {
          subject: t("models.users.user_one"),
        })
      ),
    }
  );

  const [isOpen, setOpen] = useState<boolean>(false);

  const { data: customers, isLoading } = useQueryPopups(
    portalApi.endpoints.listCustomers.useQuery(undefined, {
      skip: !isOpen || !canReadCustomers,
    }),
    { errorVariant: "warning" }
  );

  const visibleRoles: UserDisplayRole[] = [
    UserDisplayRole.operator_basic,
    UserDisplayRole.operator_advanced,
    UserDisplayRole.farm_manager,
  ];

  return (
    <>
      {hotkey && (
        <GlobalHotKeys
          keyMap={{
            INVITE_USER: {
              name: titleCase(
                t("utils.actions.inviteLong", {
                  subject: t("models.users.user_one"),
                })
              ),
              action: "keyup",
              sequence: hotkey,
            },
          }}
          handlers={{
            INVITE_USER: () => setOpen(true),
          }}
        />
      )}
      <Button
        className="text-white"
        startIcon={<AddIcon />}
        onClick={() => setOpen(true)}
      >
        <span className="hidden sm:inline">
          {titleCase(
            t("utils.actions.inviteLong", {
              subject: t("models.users.user_one"),
            })
          )}
        </span>
        <span className="sm:hidden">{t("utils.actions.invite")}</span>
      </Button>
      <Dialog open={isOpen} onClose={() => setOpen(false)}>
        <DialogTitle>
          {titleCase(
            t("utils.actions.inviteLong", {
              subject: t("models.users.user_one"),
            })
          )}
        </DialogTitle>
        <Formik
          enableReinitialize
          initialValues={{
            customer: "",
            email: "",
            role: undefined,
          }}
          validationSchema={object({
            customer: string(),
            email: string().email().required(),
            role: number().required(),
          })}
          onSubmit={async ({ email, customer, role }) => {
            if (canReadCustomers && !customer) {
              dispatch(
                addNotification({
                  message: t("components.InviteUser.errors.customerRequired"),
                  variant: "error",
                })
              );
              return;
            }
            const { error } = await inviteUser({
              email,
              customerId: customer === "" ? undefined : Number(customer),
              role,
            });
            if (!error) {
              setOpen(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, dirty }) => (
            <Form>
              <DialogContent className="flex flex-col gap-4 pt-2">
                {canReadCustomers && (
                  <Field
                    component={Select}
                    name="customer"
                    disabled={isLoading}
                    label={capitalize(t("models.customers.customer_one"))}
                    className={classes("min-w-48")}
                    placeholder={
                      isLoading ? t("components.Loading.placeholder") : ""
                    }
                  >
                    {customers?.map(({ db, name }) => (
                      <MenuItem value={String(db?.id)} key={String(db?.id)}>
                        {name}
                      </MenuItem>
                    ))}
                  </Field>
                )}
                <Field
                  // the whole dialog opens as a response to user action,
                  // so it's fine to autofocus the email field
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  component={TextField}
                  label={t("models.users.fields.email")}
                  name="email"
                  type="email"
                  fullWidth
                />
                <Field
                  component={Select}
                  input={
                    <OutlinedInput
                      label={t("models.users.role", { count: 1 })}
                    />
                  }
                  name="role"
                  disabled={!canUpdatePermissions}
                  className="w-full"
                  label={t("models.users.role_one")}
                  renderValue={(encoded: string) => {
                    const role = Number(encoded);
                    const slug = userDisplayRoleToJSON(role);
                    // carbon.actions.compareKeys.ignoreDynamic
                    return t(`models.users.roles.${slug}`);
                  }}
                >
                  {visibleRoles.map((role) => {
                    const slug = userDisplayRoleToJSON(role);
                    return (
                      <MenuItem value={role} key={role}>
                        {/* carbon.actions.compareKeys.ignoreDynamic */}
                        {t(`models.users.roles.${slug}`)}
                      </MenuItem>
                    );
                  })}
                </Field>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  disabled={isSubmitting}
                  onClick={() => setOpen(false)}
                >
                  {t("utils.actions.cancel")}
                </Button>
                <Button
                  {...BLUE_LOADING_BUTTON}
                  disabled={!dirty}
                  loading={isSubmitting}
                  onClick={submitForm}
                  startIcon={<InviteIcon />}
                >
                  {t("utils.actions.invite")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
